//================================================================
//  Component: Tab View Mobile
//================================================================

//  Purpose: Simple way to build a tab view for content & supports query strings 'https://example.com?view=default'

//  Properties:
//    - defaultView = {A string, default view for the tabular view}
//    - tabs = {An array of objects, each object represents a tab}

//  Example:
//    <TabViewM
//      defaultView={1}
//      tabs={[
//        {title: 'Overview', content: <div>Overview content</div>, route: '/overview?view=detailed'},
//        {title: 'Pricing', content: <div>Pricing content</div>, route: '/pricing?view=default'},
//        {title: 'Support', content: <div>Support content</div>, route: '/support?view=default'},
//      ]}
//    ></TabView>    

//================================================================

//Libraries
import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

const TabViewM = ({
  defaultView,
  tabs = []
}) => {

  //------------------------------------------------------
  //  React Router
  //------------------------------------------------------

  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  //------------------------------------------------------
  //  useStates
  //------------------------------------------------------

  //Visibilty of content > depends on click event for each tab
  const [activeTab, setActiveTab] = useState(defaultView);

  //------------------------------------------------------
  //  Functions
  //------------------------------------------------------

  // When a tab is clicked --> Navigate to the correct tab & update state
  const handleTabClick = (id, route) => {

    setActiveTab(id);
    navigate(route);

  };

  // Determine tab navigation style
  const getTabStyle = (id) => {
    const baseStyle = 'flex-1 text-center py-3 text-[16px] relative';
    const activeStyle = 'border-b-2 border-[#00B3B3] font-semibold text-[#000]';
    const inactiveStyle = 'text-[#000]';

    const styles = [
      baseStyle,
      activeTab === id ? activeStyle : inactiveStyle
    ];

    return styles.filter(Boolean).join(' ');
  };

  //------------------------------------------------------
  //  useEffects
  //------------------------------------------------------

  // Check route on load
  useEffect(() => {
    if (!location.search) return;

    const matchedTab = tabs.find(tab =>
      `${location.pathname}${location.search}` === tab.route
    );

    if (matchedTab) {
      setActiveTab(matchedTab.id);
    } else {
      setActiveTab('not-found');
    }
  }, [location.pathname, location.search, searchParams, tabs]);

  
  //------------------------------------------------------
  //  HTML
  //------------------------------------------------------

  return (
    <div className='w-full'>

      {/* Tabs */}
      <div className='flex border-b border-[#D8D8D8]'>
        {
          tabs.map(tab => (
            <div key={tab.id} className={getTabStyle(tab.id)} onClick={() => handleTabClick(tab.id, tab.route)} hidden={tab?.hidden}>
              {tab.title}
              {
                activeTab === tab.id && tab.tag > 0 &&
                (
                  <span className='absolute top-4 right-5 bg-[#000] text-white text-xs rounded-full w-[26px] h-[26px] flex items-center justify-center'>
                    {tab.tag}
                  </span>
                )
              }
            </div>
          ))
        }
      </div>

      {/* Content */}
      <div className='p-[18px]'>
        {tabs.find(tab => tab.id === activeTab)?.content}
      </div>

      {/* Not-Found Page */}
      {activeTab === 'not-found' && (
        <div className='p-[18px] text-center'>
          <h3 className='text-xl mb-4'>Error 404 - Page not found</h3>
          <p className='mb-4'>
            If the error persists, please reach out to our support email via email <a href="mailto:33d55468.lendlease.onmicrosoft.com@amer.teams.ms" target="_blank" rel="noopener noreferrer"> here</a> and include the above url.
          </p>
          <button
            className='bg-[#00B3B3] text-white px-4 py-2 rounded'
            onClick={() => navigate('/')}
          >
            Return Home
          </button>
        </div>
      )}
    </div>
  );
};

export default TabViewM;